/**
 * Return Protection Controller
 * ============================
 *
 * Architecture: DISPLAY-ONLY UNTIL CLICK
 * --------------------------------------
 * The protection variant is NOT added to the cart while the customer browses.
 * Instead, the UI calculates and displays the appropriate tier visually based
 * on the eligible subtotal. The variant is only added at the moment the user
 * clicks "Continue with Checkout+", after which we redirect to /checkout.
 *
 * If the user navigates back from checkout (any page load with the
 * `rp_pending_checkout` sessionStorage flag set), the variant is stripped from
 * the cart automatically.
 *
 * Lifecycle:
 *   1) Page load → if cart has protection variant (lingering / returned-from-
 *      checkout state), strip it. Clear pending flag.
 *   2) UI render → compute eligible subtotal, pick tier, show in footer.
 *   3) "Continue with Checkout+" → set pending flag, add variant, navigate.
 *   4) "checkout without" → navigate directly.
 *   5) Return to site → step 1 strips it again. Loop closed.
 *
 * Tier matching: inclusive-low, exclusive-high.
 *   $100.00 exactly  -> Tier 2
 *   $2000.00 exactly -> Tier 9
 *
 * Excluded tags (case-insensitive, whitespace-tolerant):
 *   - clearance
 *   - hat shaping
 */
(function () {
  'use strict';

  // ---------------------------------------------------------------------------
  // Configuration
  // ---------------------------------------------------------------------------

  var TIERS = [
    { id: 52068589043943, min: 0,       max: 100,    price: 300   }, // Tier 1: $3.00
    { id: 52068589076711, min: 100,     max: 500,    price: 500   }, // Tier 2: $5.00
    { id: 52068589109479, min: 500,     max: 750,    price: 750   }, // Tier 3: $7.50
    { id: 52068589142247, min: 750,     max: 1000,   price: 1000  }, // Tier 4: $10.00
    { id: 52068589175015, min: 1000,    max: 1250,   price: 1250  }, // Tier 5: $12.50
    { id: 52068589207783, min: 1250,    max: 1500,   price: 1500  }, // Tier 6: $15.00
    { id: 52068589240551, min: 1500,    max: 1750,   price: 1750  }, // Tier 7: $17.50
    { id: 52068589273319, min: 1750,    max: 2000,   price: 2000  }, // Tier 8: $20.00
    { id: 52068589306087, min: 2000,    max: Infinity, price: 2500 }  // Tier 9: $25.00
  ];

  // Tags that exclude a single item from the protection calculation but
  // otherwise allow return protection to show (with banner + Final Sale tag
  // in mixed carts).
  var EXCLUDED_TAGS = ['clearance']; // lowercase; matched case-insensitively

  // Tags that suppress return protection ENTIRELY when present anywhere in
  // the cart. If any item has one of these tags, no RP UI is shown at all:
  // no tier row, no banner, no Final Sale tags, no opt-out link.
  var KILL_SWITCH_TAGS = ['hat shaping'];

  // Combined set used by the tag-map hydration code (it doesn't care about
  // the semantic difference — it just needs to know which tags to record).
  var ALL_RELEVANT_TAGS = EXCLUDED_TAGS.concat(KILL_SWITCH_TAGS);

  var PENDING_CHECKOUT_FLAG = 'rp_pending_checkout';

  // Set of all tier variant IDs for fast lookup
  var TIER_VARIANT_IDS = TIERS.reduce(function (acc, t) {
    acc[t.id] = true;
    return acc;
  }, {});

  // ---------------------------------------------------------------------------
  // State
  // ---------------------------------------------------------------------------

  var lastKnownTier = null;   // tier object for current eligible subtotal, or null
  var lastKnownState = {       // last computed view of the cart for UI consumers
    eligibleSubtotal: 0,
    hasEligibleItems: false,
    hasExcludedItems: false,
    tier: null
  };

  // ---------------------------------------------------------------------------
  // Utilities
  // ---------------------------------------------------------------------------

  function log() {
    if (window.ReturnProtectionDebug) {
      console.log.apply(console, ['[RP]'].concat([].slice.call(arguments)));
    }
  }

  function isProtectionVariant(item) {
    return !!TIER_VARIANT_IDS[item.variant_id];
  }

  /**
   * Return the list of relevant tags on an item, or null if we don't know.
   * Used by both isItemExcluded and isItemKillSwitch — pulling tag lookup
   * out so we don't duplicate it.
   */
  function getItemTags(item) {
    var rawTags = null;

    if (Array.isArray(item.product_tags)) {
      rawTags = item.product_tags;
    } else if (typeof item.product_tags === 'string') {
      rawTags = item.product_tags.split(',');
    } else if (window.__ReturnProtectionTagMap && item.handle && Object.prototype.hasOwnProperty.call(window.__ReturnProtectionTagMap, item.handle)) {
      rawTags = window.__ReturnProtectionTagMap[item.handle];
    }

    if (rawTags === null) return null;

    return rawTags.map(function (t) {
      return String(t || '').trim().toLowerCase();
    });
  }

  /**
   * Tri-state per-item exclusion check (clearance only).
   * Returns true/false definitively, or null if tags are unknown.
   *
   * "Excluded" items are removed from the protection calculation but otherwise
   * coexist with protection — banner shows in mixed carts, Final Sale tag
   * appears on the item, protection covers the remaining eligible items.
   */
  function isItemExcluded(item) {
    var tags = getItemTags(item);
    if (tags === null) return null;
    for (var i = 0; i < EXCLUDED_TAGS.length; i++) {
      if (tags.indexOf(EXCLUDED_TAGS[i]) !== -1) return true;
    }
    return false;
  }

  /**
   * Tri-state kill-switch check (hat shaping).
   * Returns true/false definitively, or null if tags are unknown.
   *
   * If ANY item in the cart has a kill-switch tag, return protection UI is
   * suppressed entirely across the whole cart — no tier row, no banner, no
   * Final Sale tags, no protected checkout button.
   */
  function isItemKillSwitch(item) {
    var tags = getItemTags(item);
    if (tags === null) return null;
    for (var i = 0; i < KILL_SWITCH_TAGS.length; i++) {
      if (tags.indexOf(KILL_SWITCH_TAGS[i]) !== -1) return true;
    }
    return false;
  }

  /**
   * Fetch product tags for handles not yet in the tag map.
   * Uses /products/{handle}.js — Shopify's storefront product JSON.
   */
  function hydrateUnknownHandles(cart) {
    var map = window.__ReturnProtectionTagMap || (window.__ReturnProtectionTagMap = {});
    var unknown = [];
    (cart.items || []).forEach(function (item) {
      if (isProtectionVariant(item)) return;
      if (!item.handle) return;
      if (!Object.prototype.hasOwnProperty.call(map, item.handle)) {
        unknown.push(item.handle);
      }
    });

    if (unknown.length === 0) return Promise.resolve();

    log('hydrating unknown handles', unknown);
    return Promise.all(unknown.map(function (handle) {
      return fetch('/products/' + encodeURIComponent(handle) + '.js', { credentials: 'same-origin' })
        .then(function (r) { return r.ok ? r.json() : null; })
        .then(function (product) {
          if (!product || !Array.isArray(product.tags)) {
            map[handle] = [];
            return;
          }
          var relevant = product.tags.filter(function (t) {
            var n = String(t || '').trim().toLowerCase();
            return ALL_RELEVANT_TAGS.indexOf(n) !== -1;
          }).map(function (t) {
            return String(t).trim().toLowerCase();
          });
          map[handle] = relevant;
          log('hydrated', handle, '->', relevant);
        })
        .catch(function () {
          map[handle] = [];
        });
    }));
  }

  /**
   * Analyze a cart object: classify items as eligible, excluded, kill-switch,
   * or protection. Returns the eligible subtotal in cents and presence flags.
   *
   * Note: the eligible subtotal is computed regardless of kill-switch state.
   * The caller decides what to do when killSwitchActive is true (typically:
   * suppress all UI). This keeps analyzeCart pure.
   */
  function analyzeCart(cart) {
    var eligibleSubtotal = 0;
    var hasEligibleItems = false;
    var hasExcludedItems = false;
    var killSwitchActive = false;
    var hasAnyItems = false;

    (cart.items || []).forEach(function (item) {
      if (isProtectionVariant(item)) return;
      hasAnyItems = true;

      if (isItemKillSwitch(item) === true) {
        killSwitchActive = true;
      }

      var excluded = isItemExcluded(item);
      if (excluded === true) {
        hasExcludedItems = true;
        return;
      }
      // false OR null (unknown — treat as eligible by default)
      hasEligibleItems = true;
      eligibleSubtotal += item.final_line_price;
    });

    return {
      eligibleSubtotal: eligibleSubtotal,
      hasEligibleItems: hasEligibleItems,
      hasExcludedItems: hasExcludedItems,
      killSwitchActive: killSwitchActive,
      hasAnyItems: hasAnyItems
    };
  }

  function pickTier(eligibleSubtotalCents) {
    var subtotal = eligibleSubtotalCents / 100;
    for (var i = 0; i < TIERS.length; i++) {
      var t = TIERS[i];
      if (subtotal >= t.min && subtotal < t.max) return t;
    }
    return TIERS[TIERS.length - 1];
  }

  /**
   * Strip ALL protection variant lines from the cart.
   * Returns a promise that resolves to the final cart.
   * If no protection lines exist, resolves immediately without an API call.
   */
  function stripProtectionFromCart() {
    return fetch('/cart.js', { credentials: 'same-origin' })
      .then(function (r) { return r.json(); })
      .then(function (cart) {
        var existing = (cart.items || []).filter(isProtectionVariant);
        if (existing.length === 0) {
          log('no protection variants to strip');
          return cart;
        }

        log('stripping protection variants', existing.map(function (i) { return i.variant_id; }));
        var updates = {};
        existing.forEach(function (item) { updates[item.variant_id] = 0; });

        return fetch('/cart/update.js', {
          method: 'POST',
          credentials: 'same-origin',
          headers: { 'Content-Type': 'application/json', 'Accept': 'application/json' },
          body: JSON.stringify({ updates: updates })
        }).then(function (r) { return r.json(); });
      });
  }

  /**
   * Analyze the cart without mutating it. Computes the tier the customer would
   * get if they checked out right now. Dispatches an event so UI listeners can
   * update.
   */
  function refresh() {
    return fetch('/cart.js', { credentials: 'same-origin' })
      .then(function (r) { return r.json(); })
      .then(function (cart) {
        return hydrateUnknownHandles(cart).then(function () { return cart; });
      })
      .then(function (cart) {
        var analysis = analyzeCart(cart);

        // Kill switch: if any item has a kill-switch tag (e.g. hat shaping),
        // suppress return protection entirely. The tier is forced to null and
        // the UI listener will hide all RP elements.
        var tier;
        if (analysis.killSwitchActive) {
          tier = null;
        } else {
          tier = analysis.hasEligibleItems ? pickTier(analysis.eligibleSubtotal) : null;
        }

        lastKnownTier = tier;
        lastKnownState = {
          eligibleSubtotal: analysis.eligibleSubtotal,
          hasEligibleItems: analysis.hasEligibleItems,
          hasExcludedItems: analysis.hasExcludedItems,
          killSwitchActive: analysis.killSwitchActive,
          tier: tier
        };

        log('refresh', lastKnownState);

        document.dispatchEvent(new CustomEvent('return-protection:refreshed', {
          detail: {
            tier: tier,
            hasEligibleItems: analysis.hasEligibleItems,
            hasExcludedItems: analysis.hasExcludedItems,
            killSwitchActive: analysis.killSwitchActive,
            cart: cart
          }
        }));

        return cart;
      })
      .catch(function (err) {
        console.error('[ReturnProtection] refresh failed', err);
      });
  }

  // Debounced refresh for rapid cart mutations
  var refreshTimer = null;
  function debouncedRefresh(delay) {
    clearTimeout(refreshTimer);
    refreshTimer = setTimeout(refresh, delay || 200);
  }

  // ---------------------------------------------------------------------------
  // Checkout flow — commit-then-redirect
  // ---------------------------------------------------------------------------

  /**
   * Commit return protection (add the variant) and redirect to checkout.
   * Called when the user clicks "Continue with Checkout+".
   *
   * If no tier is known (no eligible items), this is a no-op redirect.
   * If add fails, we redirect anyway — the user shouldn't be stranded.
   */
  function checkoutWithProtection() {
    log('checkout with protection — tier:', lastKnownTier);

    var goToCheckout = function () { window.location.href = '/checkout'; };

    if (!lastKnownTier) {
      log('no tier — redirecting without adding');
      goToCheckout();
      return;
    }

    // Set the flag BEFORE adding, so even if navigation interrupts the add
    // response, the next page load will know to strip the variant if it lingers.
    try {
      sessionStorage.setItem(PENDING_CHECKOUT_FLAG, '1');
    } catch (e) {
      log('sessionStorage unavailable');
    }

    var addPromise = fetch('/cart/add.js', {
      method: 'POST',
      credentials: 'same-origin',
      headers: { 'Content-Type': 'application/json', 'Accept': 'application/json' },
      body: JSON.stringify({ id: lastKnownTier.id, quantity: 1 })
    }).then(function (r) {
      if (!r.ok) throw new Error('add failed: ' + r.status);
      return r.json();
    });

    addPromise.then(goToCheckout, function (err) {
      console.error('[ReturnProtection] failed to add protection — redirecting anyway', err);
      goToCheckout();
    });

    // Safety net: redirect after 4s no matter what
    setTimeout(goToCheckout, 4000);
  }

  /**
   * Skip protection — just go to checkout. No mutation needed since protection
   * isn't in the cart in steady state.
   */
  function checkoutWithoutProtection() {
    log('checkout without protection');
    window.location.href = '/checkout';
  }

  // ---------------------------------------------------------------------------
  // Page-load reconciliation
  // ---------------------------------------------------------------------------

  /**
   * Run on every page load. Strips any protection variant in the cart (whether
   * from a previous checkout attempt or stale state) and refreshes the UI.
   * Clears the pending-checkout sessionStorage flag if it was set.
   */
  function reconcile() {
    var flagWasSet = false;
    try {
      flagWasSet = sessionStorage.getItem(PENDING_CHECKOUT_FLAG) === '1';
      if (flagWasSet) {
        sessionStorage.removeItem(PENDING_CHECKOUT_FLAG);
      }
    } catch (e) {
      log('sessionStorage read failed', e);
    }

    log('reconcile — pending flag was:', flagWasSet);

    // Strip protection regardless of flag. If protection is in the cart at
    // page load, by definition it shouldn't be (user is browsing, not
    // checking out). The flag just informs whether this was "expected" or
    // not — useful for debugging but not for behavior.
    return stripProtectionFromCart().then(function () {
      return refresh();
    });
  }

  // ---------------------------------------------------------------------------
  // Public API
  // ---------------------------------------------------------------------------

  window.ReturnProtection = {
    refresh: refresh,
    checkoutWithProtection: checkoutWithProtection,
    checkoutWithoutProtection: checkoutWithoutProtection,
    getTiers: function () { return TIERS.slice(); },
    getCurrentTier: function () { return lastKnownTier; },
    getState: function () {
      return {
        tier: lastKnownState.tier,
        eligibleSubtotal: lastKnownState.eligibleSubtotal,
        hasEligibleItems: lastKnownState.hasEligibleItems,
        hasExcludedItems: lastKnownState.hasExcludedItems,
        killSwitchActive: lastKnownState.killSwitchActive
      };
    },
    isExcluded: isItemExcluded
  };

  // ---------------------------------------------------------------------------
  // Event wiring
  // ---------------------------------------------------------------------------

  function init() {
    log('init');

    // Reconcile, then refresh.
    reconcile();

    // Listen for cart-mutation events from Impulse and our fetch monkey-patch
    [
      'ajaxProduct:added',
      'ajaxCart.afterCartLoad',
      'cart:updated',
      'cart:build'
    ].forEach(function (evt) {
      document.addEventListener(evt, function () {
        log('event', evt);
        debouncedRefresh(150);
      });
    });

    // Catch direct fetch cart mutations from anywhere else
    var originalFetch = window.fetch;
    window.fetch = function (input) {
      var url = typeof input === 'string' ? input : (input && input.url) || '';
      var isCartMutation = /\/cart\/(add|change|update|clear)/.test(url);
      var promise = originalFetch.apply(this, arguments);
      if (isCartMutation) {
        promise.then(function () { debouncedRefresh(200); }, function () {});
      }
      return promise;
    };
  }

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', init);
  } else {
    init();
  }
})();